import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import * as EarnsSelectors from '../+state/selectors/earns.selectors';
import * as OverviewSelectors from '../+state/selectors/overview.selectors';
import * as SummarySelectors from '../+state/selectors/summary.selectors';
import { DataLayerAssetsService } from '../services/analytics/data-layer-assets.service';
import { DataLayerAnalyticsService } from '../services/analytics/data-layer.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsHelperFacade {
  constructor(
    private readonly store: Store,
    private readonly dataLayer: DataLayerAnalyticsService,
    private readonly assetService: DataLayerAssetsService
  ) {}

  setupAnalyticsWithAssets(): void {
    let completedCount = 0;
    const totalSubscriptions = 10;
    let hasCalledService = false;

    const checkCompletion = () => {
      completedCount = completedCount + 1;
      if (completedCount === totalSubscriptions && !hasCalledService) {
        hasCalledService = true;
        this.dataLayer.sendAnalyticsWithAssets();
      }
    };

    this.store
      .select(SummarySelectors.getAmexSkyClubTracker)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.cards)
          this.assetService.setSkyClubAssets(this.dataLayer.assets, data);
        checkCompletion();
      });

    this.store
      .select(EarnsSelectors.getBanner)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.offer)
          this.assetService.setAmexBannerAssets(this.dataLayer.assets, data);
        checkCompletion();
      });

    this.store
      .select(OverviewSelectors.getSnapshot)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.activities?.length > 0)
          this.assetService.setSnapshotAssets(this.dataLayer.assets, data);
        checkCompletion();
      });

    this.store
      .select(OverviewSelectors.getQuickLinks)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.cards) this.dataLayer.setQuickLinkAssets(data);
        checkCompletion();
      });

    this.store
      .select(EarnsSelectors.getOffers)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.cards) this.dataLayer.setOfferAssets(data);
        checkCompletion();
      });

    this.store
      .select(SummarySelectors.getCards)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.cards) this.dataLayer.setCardAssets(data);
        checkCompletion();
      });

    this.store
      .select(SummarySelectors.getStatus)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.title) this.dataLayer.setSummaryAssets(data);
        checkCompletion();
      });

    this.store
      .select(OverviewSelectors.getUpgrades)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.cards)
          this.assetService.setUpGrades(this.dataLayer.assets, data);
        checkCompletion();
      });

    this.store
      .select(OverviewSelectors.getMicroMoments)
      .pipe(
        filter((data) => data?.loaded),
        take(1)
      )
      .subscribe((data) => {
        if (data.title)
          this.assetService.setMicroMoments(this.dataLayer.assets, data);
        checkCompletion();
      });

    this.store
      .select(EarnsSelectors.getPartnersFlag)
      .pipe(
        filter((data) => !!data),
        take(1)
      )
      .subscribe(() => {
        this.assetService.setPartners(this.dataLayer.assets);
        checkCompletion();
      });
  }
}
